import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  signOut as amplifySignOut,
  resetPassword,
  confirmResetPassword,
  fetchAuthSession,
} from "aws-amplify/auth";
import "./amplifyConfig";

type AuthStatus = "loading" | "signedOut" | "signedIn";

type AuthContextValue = {
  status: AuthStatus;
  idToken?: string;
  email?: string;
  // Pro is a paid tier now - signing in alone no longer implies it, this
  // reflects the Cognito ID token's custom:plan claim (pro or enterprise).
  isPro: boolean;
  // Unset custom:plan defaults to "free", matching the backend authorizer's
  // same default - only an explicit "enterprise" value unlocks Enterprise-only
  // features (PDF export, multiple named portfolios).
  isEnterprise: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  // Re-reads the ID token into this context's own state - needed after a
  // Stripe checkout forces a fresh token, since fetchAuthSession() alone
  // doesn't update the isPro/isEnterprise values already handed out here.
  refreshSession: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = (props: { children: ReactNode }) => {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [idToken, setIdToken] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [plan, setPlan] = useState<string>();

  const refresh = async (forceRefresh = false) => {
    try {
      const session = await fetchAuthSession({ forceRefresh });
      const token = session.tokens?.idToken;
      if (token) {
        setIdToken(token.toString());
        setEmail(token.payload.email as string | undefined);
        setPlan((token.payload["custom:plan"] as string | undefined) || "free");
        setStatus("signedIn");
      } else {
        setIdToken(undefined);
        setEmail(undefined);
        setPlan(undefined);
        setStatus("signedOut");
      }
    } catch {
      setIdToken(undefined);
      setEmail(undefined);
      setPlan(undefined);
      setStatus("signedOut");
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextValue = {
    status,
    idToken,
    email,
    isPro: status === "signedIn" && (plan === "pro" || plan === "enterprise"),
    isEnterprise: status === "signedIn" && plan === "enterprise",
    signIn: async (username, password) => {
      await amplifySignIn({ username, password });
      await refresh();
    },
    signUp: async (username, password) => {
      await amplifySignUp({ username, password, options: { userAttributes: { email: username } } });
    },
    confirmSignUp: async (username, code) => {
      await amplifyConfirmSignUp({ username, confirmationCode: code });
    },
    signOut: async () => {
      await amplifySignOut();
      await refresh();
    },
    forgotPassword: async (username) => {
      await resetPassword({ username });
    },
    confirmForgotPassword: async (username, code, newPassword) => {
      await confirmResetPassword({ username, confirmationCode: code, newPassword });
    },
    refreshSession: () => refresh(true),
  };

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
};
